'use client';

import { c as _c } from "react/compiler-runtime";
import { type FundingProvider, fundingLocationLabel, type OnrampClient } from '@morfi/onramp-sdk';
import { type FundingCheckout, useFundingProfile, useFundingProfileContact } from '@morfi/onramp-sdk/react';
import type { MoonPayWebProvider } from '@morfi/onramp-sdk/web';
import { useTranslations } from 'next-intl';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { getCurrencyByCode } from '@/config/currencies';
import { logger } from '@/lib/telemetry/logger';
import { useCurrencyStore } from '@/stores/currency';
import { FundingConnectionRequired } from '../checkout/funding-connection-required';
import { FundingDevtoolsLink } from '../devtools/funding-devtools-link';
import { FundingDevtoolsScreen } from '../devtools/funding-devtools-page';
import { useFundingStage } from '../shared/funding-chrome';
import { FundingLoading } from '../shared/funding-loading';
import { VerificationForm } from '../verification/funding-verification';
import { FundingCurrency } from './funding-currency';
import { FundingDisconnect } from './funding-disconnect';
import { FundingMethodLimits } from './funding-method-limits';
import { FundingProfileAddCard } from './funding-profile-add-card';
import { FundingProfileCards } from './funding-profile-cards';
interface Props {
  client: OnrampClient;
  provider: FundingProvider & Pick<MoonPayWebProvider, 'setupAddCard'>;
  connection: FundingCheckout['profileConnection'];
  connectionPending?: boolean;
  sessionProofId: string;
  currency: string;
  decimals: number;
  verificationError?: boolean;
  canDisconnect: boolean;
  disconnectBusy: boolean;
  onDisconnect(): Promise<boolean>;
  onBack(): void;
  onUpdated(): void;
  onRefreshEligibility(): void;
  onVerify(): void;
}
export function FundingProfile(props) {
  const $ = _c(206);
  const {
    connectionPending: t0
  } = props;
  const connectionPending = t0 === undefined ? false : t0;
  const t = useTranslations("onramp");
  const [editing, setEditing] = useState(null);
  const selectedCurrency = useCurrencyStore(_temp);
  let t1;
  if ($[0] !== connectionPending || $[1] !== props.client || $[2] !== props.connection || $[3] !== props.currency || $[4] !== props.decimals || $[5] !== props.provider || $[6] !== props.sessionProofId) {
    t1 = {
      client: props.client,
      sessionProofId: props.sessionProofId,
      currency: props.currency,
      decimals: props.decimals,
      provider: props.provider,
      connection: props.connection,
      connectionPending,
      includeLimits: false
    };
    $[0] = connectionPending;
    $[1] = props.client;
    $[2] = props.connection;
    $[3] = props.currency;
    $[4] = props.decimals;
    $[5] = props.provider;
    $[6] = props.sessionProofId;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const profile = useFundingProfile(t1);
  let t2;
  if ($[8] !== connectionPending || $[9] !== profile.data?.limitsUnavailable || $[10] !== profile.data?.savedCardsUnavailable || $[11] !== profile.error) {
    t2 = () => {
      if (!connectionPending && (profile.error || profile.data?.limitsUnavailable || profile.data?.savedCardsUnavailable)) {
        logger.warn("onramp.profile_failed", {
          failed: true,
          limitsUnavailable: Boolean(profile.data?.limitsUnavailable),
          savedCardsUnavailable: Boolean(profile.data?.savedCardsUnavailable)
        });
      }
    };
    $[8] = connectionPending;
    $[9] = profile.data?.limitsUnavailable;
    $[10] = profile.data?.savedCardsUnavailable;
    $[11] = profile.error;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const t3 = profile.data?.limitsUnavailable;
  const t4 = profile.data?.savedCardsUnavailable;
  let t5;
  if ($[13] !== connectionPending || $[14] !== profile.error || $[15] !== t3 || $[16] !== t4) {
    t5 = [connectionPending, profile.error, t3, t4];
    $[13] = connectionPending;
    $[14] = profile.error;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  useEffect(t2, t5);
  let t6;
  if ($[18] !== t) {
    t6 = t("profile_title");
    $[18] = t;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  useFundingStage(t6, false, props.onBack, !editing);
  if (editing === "devtools") {
    let t7;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <FundingDevtoolsScreen onBack={() => setEditing(null)} />;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    return t7;
  }
  if (editing === "currency") {
    let t7;
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <FundingCurrency onBack={() => setEditing(null)} />;
      $[21] = t7;
    } else {
      t7 = $[21];
    }
    return t7;
  }
  if (editing === "card") {
    let t7;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = () => setEditing(null);
      $[22] = t7;
    } else {
      t7 = $[22];
    }
    let t8;
    if ($[23] !== profile) {
      t8 = () => {
        setEditing(null);
        profile.refresh();
      };
      $[23] = profile;
      $[24] = t8;
    } else {
      t8 = $[24];
    }
    let t9;
    if ($[25] !== props.provider || $[26] !== props.sessionProofId || $[27] !== t8) {
      t9 = <FundingProfileAddCard key={props.sessionProofId} provider={props.provider} onBack={t7} onComplete={t8} />;
      $[25] = props.provider;
      $[26] = props.sessionProofId;
      $[27] = t8;
      $[28] = t9;
    } else {
      t9 = $[28];
    }
    return t9;
  }
  if (editing) {
    let t7;
    if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = () => setEditing(null);
      $[29] = t7;
    } else {
      t7 = $[29];
    }
    let t8;
    if ($[30] !== profile || $[31] !== props) {
      t8 = () => {
        setEditing(null);
        profile.refresh();
        props.onUpdated();
      };
      $[30] = profile;
      $[31] = props;
      $[32] = t8;
    } else {
      t8 = $[32];
    }
    let t9;
    if ($[33] !== editing || $[34] !== props.client || $[35] !== t8) {
      t9 = <ProfileContact client={props.client} kind={editing} onBack={t7} onComplete={t8} />;
      $[33] = editing;
      $[34] = props.client;
      $[35] = t8;
      $[36] = t9;
    } else {
      t9 = $[36];
    }
    return t9;
  }
  if ((connectionPending || profile.loading) && !profile.data) {
    let t7;
    if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <FundingLoading />;
      $[37] = t7;
    } else {
      t7 = $[37];
    }
    return t7;
  }
  if (!profile.data) {
    let t7;
    if ($[38] !== t) {
      t7 = t("profile_load_error");
      $[38] = t;
      $[39] = t7;
    } else {
      t7 = $[39];
    }
    let t8;
    if ($[40] !== t7) {
      t8 = <p>{t7}</p>;
      $[40] = t7;
      $[41] = t8;
    } else {
      t8 = $[41];
    }
    const t9 = props.onUpdated;
    let t10;
    if ($[42] !== t) {
      t10 = t("retry");
      $[42] = t;
      $[43] = t10;
    } else {
      t10 = $[43];
    }
    let t11;
    if ($[44] !== props.onUpdated || $[45] !== t10) {
      t11 = <AnimatedButton onClick={t9}>{t10}</AnimatedButton>;
      $[44] = props.onUpdated;
      $[45] = t10;
      $[46] = t11;
    } else {
      t11 = $[46];
    }
    let t12;
    if ($[47] !== t11 || $[48] !== t8) {
      t12 = <div role="alert" className="flex flex-col gap-3">{t8}{t11}</div>;
      $[47] = t11;
      $[48] = t8;
      $[49] = t12;
    } else {
      t12 = $[49];
    }
    return t12;
  }
  const {
    payer,
    verification
  } = profile.data;
  const refreshing = connectionPending || profile.loading || profile.refreshing;
  const actionsDisabled = refreshing || Boolean(profile.error);
  const presentation = profile.presentation;
  const guestUnavailable = props.connection.canConnect === true && !props.connection.guest && payer.emailVerified && payer.phoneVerified && (payer.consentRequired === false || payer.consentAccepted);
  let t7;
  if ($[50] !== t) {
    t7 = t("profile_currency_location_title");
    $[50] = t;
    $[51] = t7;
  } else {
    t7 = $[51];
  }
  let t8;
  if ($[52] !== t7) {
    t8 = <h3 className="text-heading-sm">{t7}</h3>;
    $[52] = t7;
    $[53] = t8;
  } else {
    t8 = $[53];
  }
  let t9;
  if ($[54] !== t) {
    t9 = t("profile_currency_title");
    $[54] = t;
    $[55] = t9;
  } else {
    t9 = $[55];
  }
  let t10;
  if ($[56] !== t9) {
    t10 = <span className="text-foreground-secondary">{t9}</span>;
    $[56] = t9;
    $[57] = t10;
  } else {
    t10 = $[57];
  }
  let t11;
  if ($[58] !== selectedCurrency) {
    t11 = getCurrencyByCode(selectedCurrency)?.symbol;
    $[58] = selectedCurrency;
    $[59] = t11;
  } else {
    t11 = $[59];
  }
  let t12;
  if ($[60] !== selectedCurrency || $[61] !== t11) {
    t12 = <span className="ml-auto">{t11}{selectedCurrency}</span>;
    $[60] = selectedCurrency;
    $[61] = t11;
    $[62] = t12;
  } else {
    t12 = $[62];
  }
  let t13;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => setEditing("currency");
    $[63] = t13;
  } else {
    t13 = $[63];
  }
  let t14;
  if ($[64] !== t) {
    t14 = t("profile_currency_change");
    $[64] = t;
    $[65] = t14;
  } else {
    t14 = $[65];
  }
  let t15;
  if ($[66] !== t14) {
    t15 = <AnimatedButton variant="tertiary" size="sm" className="px-0 text-caption underline underline-offset-2" onClick={t13}>{t14}</AnimatedButton>;
    $[66] = t14;
    $[67] = t15;
  } else {
    t15 = $[67];
  }
  let t16;
  if ($[68] !== t10 || $[69] !== t12 || $[70] !== t15) {
    t16 = <div className="flex items-center justify-between gap-3 text-caption">{t10}{t12}{t15}</div>;
    $[68] = t10;
    $[69] = t12;
    $[70] = t15;
    $[71] = t16;
  } else {
    t16 = $[71];
  }
  let t17;
  if ($[72] !== t) {
    t17 = t("profile_location_title");
    $[72] = t;
    $[73] = t17;
  } else {
    t17 = $[73];
  }
  let t18;
  if ($[74] !== t17) {
    t18 = <span className="text-foreground-secondary">{t17}</span>;
    $[74] = t17;
    $[75] = t18;
  } else {
    t18 = $[75];
  }
  let t19;
  if ($[76] !== props.connection.quoteContext?.context || $[77] !== t) {
    t19 = fundingLocationLabel(props.connection.quoteContext?.context) ?? t("profile_location_unknown");
    $[76] = props.connection.quoteContext?.context;
    $[77] = t;
    $[78] = t19;
  } else {
    t19 = $[78];
  }
  let t20;
  if ($[79] !== t19) {
    t20 = <span className="ml-auto">{t19}</span>;
    $[79] = t19;
    $[80] = t20;
  } else {
    t20 = $[80];
  }
  const t21 = props.onRefreshEligibility;
  let t22;
  if ($[81] !== t) {
    t22 = t("profile_location_refresh");
    $[81] = t;
    $[82] = t22;
  } else {
    t22 = $[82];
  }
  let t23;
  if ($[83] !== props.onRefreshEligibility || $[84] !== refreshing || $[85] !== t22) {
    t23 = <AnimatedButton variant="tertiary" size="sm" className="px-0 text-caption underline underline-offset-2" disabled={refreshing} onClick={t21}>{t22}</AnimatedButton>;
    $[83] = props.onRefreshEligibility;
    $[84] = refreshing;
    $[85] = t22;
    $[86] = t23;
  } else {
    t23 = $[86];
  }
  let t24;
  if ($[87] !== t18 || $[88] !== t20 || $[89] !== t23) {
    t24 = <div className="flex items-center justify-between gap-3 text-caption">{t18}{t20}{t23}</div>;
    $[87] = t18;
    $[88] = t20;
    $[89] = t23;
    $[90] = t24;
  } else {
    t24 = $[90];
  }
  let t25;
  if ($[91] !== t16 || $[92] !== t24) {
    t25 = <div className="flex flex-col gap-3 rounded-control bg-background-tertiary p-4">{t16}{t24}</div>;
    $[91] = t16;
    $[92] = t24;
    $[93] = t25;
  } else {
    t25 = $[93];
  }
  let t26;
  if ($[94] !== t25 || $[95] !== t8) {
    t26 = <div className="flex flex-col gap-3">{t8}{t25}</div>;
    $[94] = t25;
    $[95] = t8;
    $[96] = t26;
  } else {
    t26 = $[96];
  }
  let t27;
  if ($[97] !== profile.error || $[98] !== props.verificationError || $[99] !== t) {
    t27 = (props.verificationError || profile.error) && <p role="alert" className="text-caption text-foreground-secondary">{t("profile_load_error")}</p>;
    $[97] = profile.error;
    $[98] = props.verificationError;
    $[99] = t;
    $[100] = t27;
  } else {
    t27 = $[100];
  }
  let t28;
  if ($[101] !== t) {
    t28 = t("profile_connected");
    $[101] = t;
    $[102] = t28;
  } else {
    t28 = $[102];
  }
  let t29;
  if ($[103] !== t28) {
    t29 = <h3 className="text-heading-sm">{t28}</h3>;
    $[103] = t28;
    $[104] = t29;
  } else {
    t29 = $[104];
  }
  let t30;
  if ($[105] !== t) {
    t30 = t("profile_guest");
    $[105] = t;
    $[106] = t30;
  } else {
    t30 = $[106];
  }
  let t31;
  if ($[107] !== t30) {
    t31 = <h4 className="text-heading-xs">{t30}</h4>;
    $[107] = t30;
    $[108] = t31;
  } else {
    t31 = $[108];
  }
  let t32;
  if ($[109] !== t) {
    t32 = t("email_address");
    $[109] = t;
    $[110] = t32;
  } else {
    t32 = $[110];
  }
  let t33;
  if ($[111] !== payer.verifiedEmail || $[112] !== t) {
    t33 = payer.verifiedEmail ?? t("profile_not_verified");
    $[111] = payer.verifiedEmail;
    $[112] = t;
    $[113] = t33;
  } else {
    t33 = $[113];
  }
  let t34;
  if ($[114] !== t) {
    t34 = t("change_email");
    $[114] = t;
    $[115] = t34;
  } else {
    t34 = $[115];
  }
  let t35;
  if ($[116] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = () => setEditing("email");
    $[116] = t35;
  } else {
    t35 = $[116];
  }
  let t36;
  if ($[117] !== actionsDisabled || $[118] !== t32 || $[119] !== t33 || $[120] !== t34) {
    t36 = <ProfileContactRow label={t32} value={t33} action={t34} disabled={actionsDisabled} onEdit={t35} />;
    $[117] = actionsDisabled;
    $[118] = t32;
    $[119] = t33;
    $[120] = t34;
    $[121] = t36;
  } else {
    t36 = $[121];
  }
  let t37;
  if ($[122] !== t) {
    t37 = t("phone_number");
    $[122] = t;
    $[123] = t37;
  } else {
    t37 = $[123];
  }
  let t38;
  if ($[124] !== payer.verifiedPhone || $[125] !== t) {
    t38 = payer.verifiedPhone ?? t("profile_not_verified");
    $[124] = payer.verifiedPhone;
    $[125] = t;
    $[126] = t38;
  } else {
    t38 = $[126];
  }
  let t39;
  if ($[127] !== t) {
    t39 = t("change_phone");
    $[127] = t;
    $[128] = t39;
  } else {
    t39 = $[128];
  }
  let t40;
  if ($[129] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = () => setEditing("phone");
    $[129] = t40;
  } else {
    t40 = $[129];
  }
  let t41;
  if ($[130] !== actionsDisabled || $[131] !== t37 || $[132] !== t38 || $[133] !== t39) {
    t41 = <ProfileContactRow label={t37} value={t38} action={t39} disabled={actionsDisabled} onEdit={t40} />;
    $[130] = actionsDisabled;
    $[131] = t37;
    $[132] = t38;
    $[133] = t39;
    $[134] = t41;
  } else {
    t41 = $[134];
  }
  let t42;
  if ($[135] !== guestUnavailable || $[136] !== props.onRefreshEligibility || $[137] !== refreshing) {
    t42 = guestUnavailable && <FundingConnectionRequired bordered={true} reason="guest_unavailable" canAuthenticate={true} onRefresh={props.onRefreshEligibility} busy={refreshing} />;
    $[135] = guestUnavailable;
    $[136] = props.onRefreshEligibility;
    $[137] = refreshing;
    $[138] = t42;
  } else {
    t42 = $[138];
  }
  let t43;
  if ($[139] !== t) {
    t43 = t("profile_kyc_title");
    $[139] = t;
    $[140] = t43;
  } else {
    t43 = $[140];
  }
  let t44;
  if ($[141] !== t43) {
    t44 = <h4 className="border-t border-border-secondary pt-3 text-heading-xs">{t43}</h4>;
    $[141] = t43;
    $[142] = t44;
  } else {
    t44 = $[142];
  }
  let t45;
  if ($[143] !== t || $[144] !== verification) {
    t45 = verification && verification.status !== "active" && <p>{t(`profile_status_${verification.status}`)}</p>;
    $[143] = t;
    $[144] = verification;
    $[145] = t45;
  } else {
    t45 = $[145];
  }
  let t46;
  if ($[146] !== presentation.requirements || $[147] !== t) {
    let t47;
    if ($[149] !== t) {
      t47 = requirement => <div key={requirement.category} className="flex items-center justify-between gap-3"><span className="text-caption">{t(`profile_requirement_${requirement.category}`)}</span><span className="text-caption text-foreground-secondary">{t(`profile_requirement_status_${requirement.status}`)}</span></div>;
      $[149] = t;
      $[150] = t47;
    } else {
      t47 = $[150];
    }
    t46 = presentation.requirements.map(t47);
    $[146] = presentation.requirements;
    $[147] = t;
    $[148] = t46;
  } else {
    t46 = $[148];
  }
  let t47;
  if ($[151] !== actionsDisabled || $[152] !== presentation.canVerify || $[153] !== profile.data.canConnect || $[154] !== props.onVerify || $[155] !== t) {
    t47 = presentation.canVerify && <><AnimatedButton onClick={props.onVerify} disabled={actionsDisabled}>{profile.data?.canConnect ? t("profile_connect_moonpay") : t("profile_complete_verification")}</AnimatedButton><p className="text-caption text-foreground-secondary">{t("profile_higher_limits")}</p></>;
    $[151] = actionsDisabled;
    $[152] = presentation.canVerify;
    $[153] = profile.data.canConnect;
    $[154] = props.onVerify;
    $[155] = t;
    $[156] = t47;
  } else {
    t47 = $[156];
  }
  let t48;
  if ($[157] !== actionsDisabled || $[158] !== props.canDisconnect || $[159] !== props.connection.verification || $[160] !== props.disconnectBusy || $[161] !== props.onDisconnect) {
    t48 = props.connection.verification ? <FundingDisconnect canDisconnect={props.canDisconnect && !actionsDisabled} busy={props.disconnectBusy} onDisconnect={props.onDisconnect} /> : null;
    $[157] = actionsDisabled;
    $[158] = props.canDisconnect;
    $[159] = props.connection.verification;
    $[160] = props.disconnectBusy;
    $[161] = props.onDisconnect;
    $[162] = t48;
  } else {
    t48 = $[162];
  }
  let t49;
  if ($[163] !== t31 || $[164] !== t36 || $[165] !== t41 || $[166] !== t42 || $[167] !== t44 || $[168] !== t45 || $[169] !== t46 || $[170] !== t47 || $[171] !== t48) {
    t49 = <div className="flex flex-col gap-3 rounded-control bg-background-tertiary p-4">{t31}{t36}{t41}{t42}{t44}{t45}{t46}{t47}{t48}</div>;
    $[163] = t31;
    $[164] = t36;
    $[165] = t41;
    $[166] = t42;
    $[167] = t44;
    $[168] = t45;
    $[169] = t46;
    $[170] = t47;
    $[171] = t48;
    $[172] = t49;
  } else {
    t49 = $[172];
  }
  let t50;
  if ($[173] !== t29 || $[174] !== t49) {
    t50 = <div className="flex flex-col gap-3">{t29}{t49}</div>;
    $[173] = t29;
    $[174] = t49;
    $[175] = t50;
  } else {
    t50 = $[175];
  }
  let t51;
  if ($[176] !== profile.data.savedCards || $[177] !== profile.data.savedCardsUnavailable || $[178] !== profile.refresh || $[179] !== props.provider || $[180] !== props.sessionProofId || $[181] !== refreshing || $[182] !== verification) {
    t51 = verification ? <FundingProfileCards key={`${props.sessionProofId}:${verification.customerId}`} provider={props.provider} onAdd={() => setEditing("card")} cards={profile.data.savedCards ?? []} unavailable={profile.data.savedCardsUnavailable ?? false} refreshing={refreshing} onRefresh={profile.refresh} /> : null;
    $[176] = profile.data.savedCards;
    $[177] = profile.data.savedCardsUnavailable;
    $[178] = profile.refresh;
    $[179] = props.provider;
    $[180] = props.sessionProofId;
    $[181] = refreshing;
    $[182] = verification;
    $[183] = t51;
  } else {
    t51 = $[183];
  }
  const t52 = !connectionPending;
  let t53;
  if ($[184] !== props.client || $[185] !== props.connection || $[186] !== props.currency || $[187] !== props.decimals || $[188] !== props.provider || $[189] !== props.sessionProofId || $[190] !== t52) {
    t53 = <FundingMethodLimits client={props.client} provider={props.provider} connection={props.connection} sessionProofId={props.sessionProofId} currency={props.currency} decimals={props.decimals} enabled={t52} />;
    $[184] = props.client;
    $[185] = props.connection;
    $[186] = props.currency;
    $[187] = props.decimals;
    $[188] = props.provider;
    $[189] = props.sessionProofId;
    $[190] = t52;
    $[191] = t53;
  } else {
    t53 = $[191];
  }
  const t54 = props.onUpdated;
  let t55;
  if ($[192] !== t) {
    t55 = t("profile_refresh");
    $[192] = t;
    $[193] = t55;
  } else {
    t55 = $[193];
  }
  let t56;
  if ($[194] !== props.onUpdated || $[195] !== refreshing || $[196] !== t55) {
    t56 = <button type="button" onClick={t54} disabled={refreshing} className="cursor-pointer text-caption text-foreground-secondary underline-offset-4 hover:underline focus-visible:underline disabled:cursor-not-allowed">{t55}</button>;
    $[194] = props.onUpdated;
    $[195] = refreshing;
    $[196] = t55;
    $[197] = t56;
  } else {
    t56 = $[197];
  }
  let t57;
  if ($[198] === Symbol.for("react.memo_cache_sentinel")) {
    t57 = <FundingDevtoolsLink onOpen={() => setEditing("devtools")} />;
    $[198] = t57;
  } else {
    t57 = $[198];
  }
  let t58;
  if ($[199] !== t26 || $[200] !== t27 || $[201] !== t50 || $[202] !== t51 || $[203] !== t53 || $[204] !== t56) {
    t58 = <div className="flex flex-col gap-5" data-private="true">{t26}{t27}{t50}{t51}{t53}{t56}{t57}</div>;
    $[199] = t26;
    $[200] = t27;
    $[201] = t50;
    $[202] = t51;
    $[203] = t53;
    $[204] = t56;
    $[205] = t58;
  } else {
    t58 = $[205];
  }
  return t58;
}
function _temp(state) {
  return state.currency;
}
function ProfileContactRow(t0) {
  const $ = _c(14);
  const {
    label,
    value,
    action,
    disabled,
    onEdit
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <p className="text-caption text-foreground-secondary">{label}</p>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== value) {
    t2 = <p className="break-all text-caption">{value}</p>;
    $[2] = value;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="min-w-0">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== action || $[8] !== disabled || $[9] !== onEdit) {
    t4 = <AnimatedButton variant="tertiary" size="sm" className="shrink-0 text-caption underline" onClick={onEdit} disabled={disabled}>{action}</AnimatedButton>;
    $[7] = action;
    $[8] = disabled;
    $[9] = onEdit;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t3 || $[12] !== t4) {
    t5 = <div className="flex items-center justify-between gap-3">{t3}{t4}</div>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
}
function ProfileContact(t0) {
  const $ = _c(7);
  const {
    client,
    kind,
    onBack,
    onComplete
  } = t0;
  const verification = useFundingProfileContact(client, kind);
  const completed = useRef(false);
  let t1;
  let t2;
  if ($[0] !== onComplete || $[1] !== verification.step) {
    t1 = () => {
      if (verification.step === "ready" && !completed.current) {
        completed.current = true;
        onComplete();
      }
    };
    t2 = [verification.step, onComplete];
    $[0] = onComplete;
    $[1] = verification.step;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== onBack || $[5] !== verification) {
    t3 = <VerificationForm verification={verification} requireConsent={false} onBack={onBack} />;
    $[4] = onBack;
    $[5] = verification;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}